import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getUserById } from "../../services/authService";
import { getSubscription } from "../../services/paymentService";
import { getUsageAnalytics } from "../../services/developerService";

const TokenBalanceCard = ({
  balance: propBalance,
  expiresIn: propExpiresIn,
  remainingDays: propRemainingDays,
  userDetails: propUserDetails,
  subscriptionDetails: propSubscriptionDetails,
  analytics: propAnalytics,
}) => {
  const { token } = useSelector((state) => state.auth);
  
  // Local states if props aren't provided
  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we have explicit props, no need to fetch
    if (propUserDetails || propBalance !== undefined || propRemainingDays !== undefined || propExpiresIn !== undefined) {
      return;
    }

    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const uRes = await getUserById(token);
        if (uRes) setUserDetails(uRes);

        try {
          const subResponse = await getSubscription(token);
          if (subResponse.success && subResponse.hasSubscription && subResponse.subscription.status === 'active') {
            setSubscriptionDetails(subResponse.subscription);
          }
        } catch (e) {
          console.error("Error fetching subscription in TokenBalanceCard:", e);
        }

        try {
          const analyticsResponse = await getUsageAnalytics('30d', token);
          setAnalytics(analyticsResponse);
        } catch (e) {
          console.error("Error fetching analytics in TokenBalanceCard:", e);
        }
      } catch (error) {
        console.error("Error loading dashboard data in TokenBalanceCard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, propUserDetails, propBalance, propRemainingDays, propExpiresIn]);

  // Determine current active objects
  const activeUser = propUserDetails || userDetails;
  const activeSub = propSubscriptionDetails || subscriptionDetails;
  const activeAnalytics = propAnalytics || analytics;

  // Calculate remaining days
  let finalRemainingDays = null;
  if (propRemainingDays !== undefined) {
    finalRemainingDays = propRemainingDays;
  } else if (propExpiresIn !== undefined) {
    finalRemainingDays = propExpiresIn;
  } else if (activeUser?.tokenExpiration) {
    const now = new Date();
    const exp = new Date(activeUser.tokenExpiration);
    const diffTime = exp.getTime() - now.getTime();
    finalRemainingDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }

  // Calculate balance
  let finalBalance = "0";
  if (propBalance !== undefined) {
    finalBalance = propBalance.toLocaleString();
  } else if (activeUser?.tokens !== undefined && activeUser.tokens !== null) {
    finalBalance = activeUser.tokens.toLocaleString();
  } else if (activeSub?.tokens !== undefined && activeSub?.tokens !== null) {
    const subscriptionTokens = activeSub.tokens;
    const tokensUsed = activeAnalytics?.totalTokens || 0;
    const available = Math.max(0, subscriptionTokens - tokensUsed);
    finalBalance = available.toLocaleString();
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#E0E4E2] shadow-sm flex-1">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-[#1D2129]">Token Balance</h3>
        <span
          className={`text-sm font-medium px-4 py-1.5 rounded-full border ${
            finalRemainingDays === null || finalRemainingDays === 0
              ? "bg-gray-50 border-gray-200 text-gray-400"
              : finalRemainingDays >= 1 && finalRemainingDays <= 5
              ? "bg-red-50 border-red-200 text-red-600"
              : finalRemainingDays >= 6 && finalRemainingDays <= 14
              ? "bg-[#FFF8EB] border-[#FFE7C1] text-[#EF8702]"
              : "bg-[#E7F3EF] border-[#D0E7DF] text-[#1E6C56]"
          }`}
        >
          {finalRemainingDays === null || finalRemainingDays === 0
            ? "expired"
            : `expires in ${finalRemainingDays} ${finalRemainingDays === 1 ? "day" : "days"}`}
        </span>
      </div>
      <div>
        <div className="text-4xl font-extrabold text-[#1D2129] mb-1">
          {loading && finalBalance === "0" ? (
            <span className="text-gray-400 text-2xl">Loading...</span>
          ) : (
            finalBalance
          )}
        </div>
        <div className="text-sm font-medium text-[#7C8087]">
          tokens available
        </div>
      </div>
    </div>
  );
};

export default TokenBalanceCard;
